import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import { formatDateBR } from '../helpers/formatDate';

// Formata numero como R$ (Intl); valor null/undefined vira '-'
function formatBRL(v) {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Mesma leitura do backend (normalizarValor): "3.500,50", "3.500" e "3500.50".
// Vazio ou invalido vira null.
function parseValor(v) {
  const t = String(v ?? '').trim();
  if (!t) return null;
  const n = t.includes(',') ? Number(t.replace(/\./g, '').replace(',', '.'))
    : /^\d{1,3}(\.\d{3})+$/.test(t) ? Number(t.replace(/\./g, ''))
      : Number(t);
  return Number.isFinite(n) ? n : null;
}

// Limite NULL aprova qualquer valor; com limite, precisa do valor para saber.
function aprovadorCobre(aprovador, valor) {
  if (aprovador.valor_limite === null || aprovador.valor_limite === undefined) return true;
  return valor !== null && valor <= Number(aprovador.valor_limite);
}

function rotuloLimite(aprovador) {
  return aprovador.valor_limite === null || aprovador.valor_limite === undefined
    ? 'sem limite'
    : `até ${formatBRL(aprovador.valor_limite)}`;
}

function formatBytes(b) {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ehImagem(mime) {
  return typeof mime === 'string' && mime.startsWith('image/');
}

const TIPO_SOLICITACAO_LABEL = { novo: 'Novo', troca: 'Troca' };
const TIPO_EQUIPAMENTO_LABEL = {
  notebook: 'Notebook', desktop: 'Desktop', monitor: 'Monitor',
  impressora: 'Impressora', celular: 'Celular', roteador: 'Roteador',
  switch: 'Switch', outro: 'Outro',
};
const STATUS_LABEL = {
  pendente: 'Pendente', aceita: 'Aceita', recusada: 'Recusada', concluida: 'Concluída',
};
const STATUS_COR = {
  pendente:  { bg: 'rgba(253,126,20,0.15)', fg: '#fd7e14', borda: 'rgba(253,126,20,0.45)' },
  aceita:    { bg: 'rgba(42,138,156,0.15)', fg: '#2A8A9C', borda: 'rgba(42,138,156,0.45)' },
  recusada:  { bg: 'rgba(220,53,69,0.15)',  fg: '#dc3545', borda: 'rgba(220,53,69,0.45)'  },
  concluida: { bg: 'rgba(40,167,69,0.15)',  fg: '#28a745', borda: 'rgba(40,167,69,0.45)'  },
};
const TIPO_COR = {
  novo:  { bg: 'rgba(42,138,156,0.12)', fg: '#2A8A9C', borda: 'rgba(42,138,156,0.4)' },
  troca: { bg: 'rgba(111,66,193,0.12)', fg: '#6f42c1', borda: 'rgba(111,66,193,0.4)' },
};

function Chip({ label, cores }) {
  if (!cores) return <span style={{ color: 'var(--portal-muted)' }}>{label}</span>;
  return (
    <span style={{
      background: cores.bg, color: cores.fg, border: `1px solid ${cores.borda}`,
      borderRadius: 8, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function ModalBase({ titulo, onClose, children, largura = 480 }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--portal-surface)', borderRadius: 12, padding: 28,
        width: '100%', maxWidth: largura, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{titulo}</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--portal-muted)' }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalNova({ onClose, onSalvo }) {
  const [form, setForm] = useState({
    tipo_solicitacao: 'troca',
    tipo_equipamento: 'notebook',
    descricao: '',
    urgente: false,
    valor_estimado: '',
    aprovador_id: '',
  });
  const [aprovadores, setAprovadores] = useState([]);
  const [arquivos, setArquivos] = useState([]); // File[]
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState(''); // avisos nao-fatais (ex.: falha em anexo apos criar)

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/solicitacoes/aprovadores`)
      .then((r) => setAprovadores(r.data?.data || []))
      .catch(() => setAprovadores([]));
  }, []);

  const valorNumero = parseValor(form.valor_estimado);
  const aprovadorEscolhido = aprovadores.find((a) => String(a.id) === String(form.aprovador_id));
  // Se o valor mudar e passar do limite de quem estava escolhido, desmarca.
  useEffect(() => {
    if (aprovadorEscolhido && !aprovadorCobre(aprovadorEscolhido, valorNumero)) {
      setForm((f) => ({ ...f, aprovador_id: '' }));
    }
  }, [aprovadorEscolhido, valorNumero]);

  const adicionarArquivos = (fileList) => {
    const novos = Array.from(fileList || []).filter((f) => f && f.size > 0);
    // Filtra >25MB antes de mandar pro servidor
    const validos = novos.filter((f) => {
      if (f.size > 25 * 1024 * 1024) {
        setAviso(`Arquivo "${f.name}" excede 25MB e foi ignorado.`);
        return false;
      }
      return true;
    });
    setArquivos((prev) => [...prev, ...validos]);
  };

  const removerArquivo = (idx) => {
    setArquivos((prev) => prev.filter((_, i) => i !== idx));
  };

  const salvar = async () => {
    if (!form.descricao.trim()) { setErro('Descricao e obrigatoria.'); return; }
    setSalvando(true); setErro(''); setAviso('');
    try {
      const payload = {
        tipo_solicitacao: form.tipo_solicitacao,
        tipo_equipamento: form.tipo_equipamento,
        descricao: form.descricao,
        urgente: form.urgente,
        // valor_estimado aceita "3500,50" (BR) ou "3500.50" — service normaliza
        valor_estimado: form.valor_estimado.trim() || null,
        aprovador_id: form.aprovador_id ? Number(form.aprovador_id) : null,
      };
      const res = await axios.post(`${API}/solicitacoes`, payload);
      let solicitacao = res.data?.data;

      // Upload sequencial dos anexos. Se algum falhar, mostra warning mas nao
      // aborta — a solicitacao ja esta criada e os outros anexos ja subiram.
      const falhas = [];
      for (const arq of arquivos) {
        try {
          const fd = new FormData();
          fd.append('arquivo', arq);
          const up = await axios.post(`${API}/solicitacoes/${solicitacao.id}/anexos`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          // Anexa o retorno na estrutura local pra o caller ja receber a lista completa
          solicitacao = {
            ...solicitacao,
            anexos: [...(solicitacao.anexos || []), up.data?.data].filter(Boolean),
          };
        } catch (e) {
          falhas.push(`${arq.name}: ${e.response?.data?.message || e.message}`);
        }
      }
      if (falhas.length) {
        setAviso(`Solicitacao criada, mas alguns anexos falharam:\n${falhas.join('\n')}`);
        onSalvo(solicitacao);
        // Nao fecha: usuario pode ver o aviso e tentar de novo
      } else {
        onSalvo(solicitacao);
        onClose();
      }
    } catch (e) {
      setErro(e.response?.data?.message || 'Erro ao criar solicitacao.');
    } finally { setSalvando(false); }
  };

  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 };
  const labelStyle = { fontSize: '0.82rem', fontWeight: 600, color: 'var(--portal-muted)' };
  const inputStyle = {
    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--portal-border)',
    background: 'var(--portal-bg)', color: 'var(--portal-text)', fontSize: '0.9rem',
  };

  return (
    <ModalBase titulo="Nova Solicitacao de Equipamento" onClose={onClose} largura={560}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Tipo de solicitacao</label>
        <select value={form.tipo_solicitacao} onChange={(e) => set('tipo_solicitacao', e.target.value)} style={inputStyle}>
          <option value="novo">Novo equipamento</option>
          <option value="troca">Troca de equipamento</option>
        </select>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Tipo de equipamento</label>
        <select value={form.tipo_equipamento} onChange={(e) => set('tipo_equipamento', e.target.value)} style={inputStyle}>
          {Object.entries(TIPO_EQUIPAMENTO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Descricao <span style={{ color: '#dc3545' }}>*</span></label>
        <textarea
          rows={4} placeholder="Descreva o equipamento necessario, justificativa, especificacoes desejadas..."
          value={form.descricao} onChange={(e) => set('descricao', e.target.value)}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Valor estimado (orcamento inicial)</label>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--portal-muted)', fontSize: '0.9rem' }}>R$</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={form.valor_estimado}
            onChange={(e) => set('valor_estimado', e.target.value)}
            style={{ ...inputStyle, paddingLeft: 34 }}
          />
        </div>
        <span style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
          Opcional. Se ja tem uma cotacao, informe o valor total previsto.
        </span>
      </div>

      {aprovadores.length > 0 && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Enviar para aprovação de</label>
          <select value={form.aprovador_id} onChange={(e) => set('aprovador_id', e.target.value)} style={inputStyle}>
            <option value="">Aprovação padrão (quem recebe o aviso)</option>
            {aprovadores.map((a) => {
              const cobre = aprovadorCobre(a, valorNumero);
              return (
                <option key={a.id} value={a.id} disabled={!cobre}>
                  {a.nome} — {rotuloLimite(a)}{cobre ? '' : ' (fora do limite)'}
                </option>
              );
            })}
          </select>
          <span style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
            {valorNumero === null
              ? 'Informe o valor estimado para liberar os aprovadores com limite.'
              : 'Só ficam disponíveis os aprovadores cujo limite cobre o valor estimado. O aviso vai pelo WhatsApp.'}
          </span>
        </div>
      )}

      <div style={fieldStyle}>
        <label style={labelStyle}>Anexos (prints, orcamentos, links...)</label>
        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); adicionarArquivos(e.dataTransfer.files); }}
          style={{
            border: '1px dashed var(--portal-border)', borderRadius: 8, padding: 12,
            background: 'var(--portal-bg)', textAlign: 'center',
          }}
        >
          <input
            type="file"
            multiple
            id="anexo-input"
            onChange={(e) => adicionarArquivos(e.target.files)}
            style={{ display: 'none' }}
          />
          <label htmlFor="anexo-input" style={{ cursor: 'pointer', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
            <i className="bi bi-paperclip me-1" />
            Clique aqui ou arraste arquivos (max 25MB cada)
          </label>
        </div>
        {arquivos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            {arquivos.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 10px', background: 'var(--portal-surface-soft)',
                border: '1px solid var(--portal-border)', borderRadius: 6, fontSize: '0.82rem',
              }}>
                <i className={`bi ${ehImagem(f.type) ? 'bi-image' : 'bi-file-earmark'}`} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.name}>
                  {f.name}
                </span>
                <span className="cell-muted" style={{ fontSize: '0.72rem' }}>{formatBytes(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removerArquivo(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                  title="Remover"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <input type="checkbox" id="urgente" checked={form.urgente} onChange={(e) => set('urgente', e.target.checked)} />
        <label htmlFor="urgente" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>Urgente</label>
      </div>
      {erro && <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 16 }}>{erro}</div>}
      {aviso && (
        <div className="feedback-banner" style={{ marginBottom: 16, background: 'rgba(253,126,20,0.12)', color: '#fd7e14', border: '1px solid rgba(253,126,20,0.35)', whiteSpace: 'pre-wrap' }}>
          {aviso}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'none', cursor: 'pointer', color: 'var(--portal-muted)' }}>
          Cancelar
        </button>
        <button type="button" onClick={salvar} disabled={salvando} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2A8A9C', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
          {salvando ? 'Enviando...' : 'Enviar Solicitacao'}
        </button>
      </div>
    </ModalBase>
  );
}

function ModalAceitar({ solicitacao, onClose, onSalvo }) {
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const salvar = async () => {
    setSalvando(true); setErro('');
    try {
      const res = await axios.patch(`${API}/solicitacoes/${solicitacao.id}/status`, {
        status: 'aceita',
        // Observacao e opcional no aceite; string vazia vira null no backend.
        observacao_atendente: observacao,
      });
      onSalvo(res.data?.data);
      onClose();
    } catch (e) {
      setErro(e.response?.data?.message || 'Erro ao aceitar solicitacao.');
    } finally { setSalvando(false); }
  };

  return (
    <ModalBase titulo={`Aceitar solicitacao #${solicitacao.id}`} onClose={onClose} largura={420}>
      <p style={{ margin: '0 0 12px', color: 'var(--portal-muted)', fontSize: '0.9rem' }}>
        Solicitante: <strong>{solicitacao.solicitante}</strong> — {TIPO_EQUIPAMENTO_LABEL[solicitacao.tipo_equipamento] || solicitacao.tipo_equipamento}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--portal-muted)' }}>
          Observacao <span style={{ color: 'var(--portal-muted)', fontWeight: 400 }}>(opcional)</span>
        </label>
        <textarea
          rows={3} placeholder="Ex.: orcamento aprovado, previsao de entrega para semana que vem..."
          value={observacao} onChange={(e) => setObservacao(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'var(--portal-bg)', color: 'var(--portal-text)', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      {erro && <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 16 }}>{erro}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'none', cursor: 'pointer', color: 'var(--portal-muted)' }}>
          Cancelar
        </button>
        <button type="button" onClick={salvar} disabled={salvando} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#28a745', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
          {salvando ? 'Aceitando...' : 'Confirmar Aceite'}
        </button>
      </div>
    </ModalBase>
  );
}

function ModalRecusar({ solicitacao, onClose, onSalvo }) {
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const salvar = async () => {
    if (!observacao.trim()) { setErro('Informe o motivo da recusa.'); return; }
    setSalvando(true); setErro('');
    try {
      const res = await axios.patch(`${API}/solicitacoes/${solicitacao.id}/status`, { status: 'recusada', observacao_atendente: observacao });
      onSalvo(res.data?.data);
      onClose();
    } catch (e) {
      setErro(e.response?.data?.message || 'Erro ao recusar solicitacao.');
    } finally { setSalvando(false); }
  };

  return (
    <ModalBase titulo={`Recusar solicitacao #${solicitacao.id}`} onClose={onClose} largura={420}>
      <p style={{ margin: '0 0 12px', color: 'var(--portal-muted)', fontSize: '0.9rem' }}>
        Solicitante: <strong>{solicitacao.solicitante}</strong> — {TIPO_EQUIPAMENTO_LABEL[solicitacao.tipo_equipamento] || solicitacao.tipo_equipamento}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--portal-muted)' }}>
          Motivo da recusa <span style={{ color: '#dc3545' }}>*</span>
        </label>
        <textarea
          rows={3} placeholder="Informe o motivo pelo qual a solicitacao nao pode ser atendida..."
          value={observacao} onChange={(e) => setObservacao(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'var(--portal-bg)', color: 'var(--portal-text)', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      {erro && <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 16 }}>{erro}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'none', cursor: 'pointer', color: 'var(--portal-muted)' }}>
          Cancelar
        </button>
        <button type="button" onClick={salvar} disabled={salvando} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#b43737', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
          {salvando ? 'Recusando...' : 'Confirmar Recusa'}
        </button>
      </div>
    </ModalBase>
  );
}

function ModalDetalhes({ solicitacao: solicitacaoInicial, onClose, onMudou }) {
  const { user, temPermissao } = useAuth();
  const [solicitacao, setSolicitacao] = useState(solicitacaoInicial);
  const [uploading, setUploading] = useState(false);
  const [erroAnexo, setErroAnexo] = useState('');

  const isGestor = temPermissao('ti:equipamentos');
  const podeMexerAnexos = isGestor || solicitacao.solicitante === user?.usuario;

  const cor = STATUS_COR[solicitacao.status];

  const recarregar = async () => {
    try {
      const res = await axios.get(`${API}/solicitacoes/${solicitacao.id}`);
      setSolicitacao(res.data?.data);
      onMudou?.(res.data?.data);
    } catch { /* silencioso */ }
  };

  const enviarAnexos = async (fileList) => {
    const arquivos = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (!arquivos.length) return;
    setUploading(true); setErroAnexo('');
    const falhas = [];
    for (const arq of arquivos) {
      if (arq.size > 25 * 1024 * 1024) {
        falhas.push(`${arq.name}: excede 25MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('arquivo', arq);
        await axios.post(`${API}/solicitacoes/${solicitacao.id}/anexos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (e) {
        falhas.push(`${arq.name}: ${e.response?.data?.message || e.message}`);
      }
    }
    if (falhas.length) setErroAnexo(falhas.join('\n'));
    await recarregar();
    setUploading(false);
  };

  const removerAnexo = async (anexo) => {
    if (!window.confirm(`Remover "${anexo.nome_original}"?`)) return;
    try {
      await axios.delete(`${API}/solicitacoes/${solicitacao.id}/anexos/${anexo.id}`);
      await recarregar();
    } catch (e) {
      setErroAnexo(e.response?.data?.message || 'Erro ao remover anexo.');
    }
  };

  const anexos = solicitacao.anexos || [];

  return (
    <ModalBase titulo={`Solicitacao #${solicitacao.id}`} onClose={onClose} largura={560}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', marginBottom: 16 }}>
        {[
          ['Tipo', <Chip label={TIPO_SOLICITACAO_LABEL[solicitacao.tipo_solicitacao]} cores={TIPO_COR[solicitacao.tipo_solicitacao]} />],
          ['Equipamento', TIPO_EQUIPAMENTO_LABEL[solicitacao.tipo_equipamento] || solicitacao.tipo_equipamento],
          ['Status', <Chip label={STATUS_LABEL[solicitacao.status] || solicitacao.status} cores={cor} />],
          ['Urgente', solicitacao.urgente ? <span style={{ color: '#dc3545', fontWeight: 600 }}>Sim</span> : 'Nao'],
          ['Solicitante', solicitacao.solicitante || '-'],
          ['Filial', solicitacao.solicitante_filial || '-'],
          ['Setor', solicitacao.solicitante_setor || '-'],
          ['Data', formatDateBR(solicitacao.created_at)],
          ['Valor estimado', <span style={{ fontWeight: 600 }}>{formatBRL(solicitacao.valor_estimado)}</span>],
          ...(solicitacao.aprovador_nome ? [['Enviada para aprovação de', solicitacao.aprovador_nome]] : []),
          // "Atendente" agora e quem aprovou ou recusou; concluidor e um campo
          // separado — antes uma coisa sobrescrevia a outra.
          ...(solicitacao.atendente
            ? [[solicitacao.status === 'recusada' ? 'Recusada por' : 'Aprovada por', solicitacao.atendente]]
            : []),
          ...(solicitacao.concluidor ? [['Concluida por', solicitacao.concluidor]] : []),
        ].map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', marginBottom: 2 }}>{k}</div>
            <div style={{ fontSize: '0.9rem' }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', marginBottom: 4 }}>Descricao</div>
        <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{solicitacao.descricao}</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Anexos ({anexos.length})</span>
          {podeMexerAnexos && (
            <label style={{ cursor: uploading ? 'default' : 'pointer', color: uploading ? 'var(--portal-muted)' : '#2A8A9C', fontSize: '0.8rem', fontWeight: 600 }}>
              <i className={`bi ${uploading ? 'bi-arrow-repeat' : 'bi-paperclip'} me-1`} />
              {uploading ? 'Enviando...' : 'Adicionar arquivo'}
              <input
                type="file"
                multiple
                disabled={uploading}
                onChange={(e) => { enviarAnexos(e.target.files); e.target.value = ''; }}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </div>
        {anexos.length === 0 ? (
          <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Nenhum anexo.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {anexos.map((a) => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', background: 'var(--portal-surface-soft)',
                border: '1px solid var(--portal-border)', borderRadius: 6, fontSize: '0.82rem',
                minWidth: 200, maxWidth: '100%',
              }}>
                {ehImagem(a.mime) && a.signed_url ? (
                  <a href={a.signed_url} target="_blank" rel="noreferrer">
                    <img
                      src={a.signed_url}
                      alt={a.nome_original}
                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                    />
                  </a>
                ) : (
                  <i className="bi bi-file-earmark" style={{ fontSize: '1.4rem' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {a.signed_url ? (
                    <a
                      href={a.signed_url}
                      target="_blank"
                      rel="noreferrer"
                      title={a.nome_original}
                      style={{
                        display: 'block', color: 'var(--portal-text)', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {a.nome_original}
                    </a>
                  ) : (
                    <span title={a.nome_original} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.nome_original}
                    </span>
                  )}
                  <span className="cell-muted" style={{ fontSize: '0.7rem' }}>{formatBytes(a.tamanho)}</span>
                </div>
                {podeMexerAnexos && (
                  <button
                    type="button"
                    onClick={() => removerAnexo(a)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                    title="Remover"
                  >
                    <i className="bi bi-trash" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {erroAnexo && (
          <div className="feedback-banner feedback-banner--error" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
            {erroAnexo}
          </div>
        )}
      </div>

      {solicitacao.observacao_atendente && (
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 8, background: solicitacao.status === 'recusada' ? 'rgba(220,53,69,0.08)' : 'rgba(40,167,69,0.08)', borderLeft: `3px solid ${solicitacao.status === 'recusada' ? '#dc3545' : '#28a745'}` }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', marginBottom: 4 }}>
            {solicitacao.status === 'recusada' ? 'Motivo da recusa' : 'Observacao no aceite'}
          </div>
          <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{solicitacao.observacao_atendente}</div>
        </div>
      )}
      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'none', cursor: 'pointer' }}>
          Fechar
        </button>
      </div>
    </ModalBase>
  );
}

// 5521965868915 -> (21) 96586-8915. Fora do padrao BR, devolve como veio.
function formatTelefone(numero) {
  const d = String(numero || '').replace(/\D/g, '').replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return numero || '-';
}

const APROVADOR_VAZIO = { id: null, contato: null, valor_limite: '' };

// Configuracao de quem aprova e ate quanto. Limite em branco = sem limite.
// O aprovador e um contato de /ti/contatos; o aviso da solicitacao sai pelo
// Martelinho para o WhatsApp desse contato.
function GerenciarAprovadores({ onClose }) {
  const [aprovadores, setAprovadores] = useState([]);
  const [form, setForm] = useState(APROVADOR_VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErro(''); };

  // Busca nos contatos do portal enquanto nenhum estiver escolhido. Debounce
  // curto para nao bater na API a cada tecla.
  useEffect(() => {
    if (form.contato) return undefined;
    let ativo = true;
    setBuscando(true);
    const t = setTimeout(() => {
      axios.get(`${API}/solicitacoes/aprovadores/contatos`, { params: { busca } })
        .then((r) => { if (ativo) setContatos(r.data?.data || []); })
        .catch(() => { if (ativo) setContatos([]); })
        .finally(() => { if (ativo) setBuscando(false); });
    }, 300);
    return () => { ativo = false; clearTimeout(t); };
  }, [busca, form.contato]);

  useEffect(() => {
    axios.get(`${API}/solicitacoes/aprovadores/cadastro`)
      .then((r) => setAprovadores(r.data?.data || []))
      .catch((e) => setErro(e.response?.data?.message || 'Não foi possível carregar os aprovadores.'))
      .finally(() => setCarregando(false));
  }, []);

  const salvar = async () => {
    if (!form.contato) {
      setErro('Escolha o contato do aprovador.');
      return;
    }
    setSalvando(true); setErro('');
    try {
      const payload = {
        contato_id: form.contato.id,
        valor_limite: String(form.valor_limite).trim() || null,
      };
      const res = form.id
        ? await axios.put(`${API}/solicitacoes/aprovadores/${form.id}`, payload)
        : await axios.post(`${API}/solicitacoes/aprovadores`, payload);
      setAprovadores(res.data?.data || []);
      setForm(APROVADOR_VAZIO);
      setBusca('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível salvar o aprovador.');
    } finally { setSalvando(false); }
  };

  const editar = (a) => {
    setErro('');
    setForm({
      id: a.id,
      // Cadastro antigo (antes da 081) nao tem contato: obriga escolher um.
      contato: a.contato_id ? { id: a.contato_id, push_name: a.nome, numero: a.numero } : null,
      valor_limite: a.valor_limite === null ? '' : Number(a.valor_limite).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
    });
  };

  const remover = async (a) => {
    if (!window.confirm(`Remover ${a.nome} dos aprovadores? Solicitações pendentes enviadas a ele passam a avisar quem recebe o aviso padrão.`)) return;
    setSalvando(true); setErro('');
    try {
      const res = await axios.delete(`${API}/solicitacoes/aprovadores/${a.id}`);
      setAprovadores(res.data?.data || []);
      if (form.id === a.id) setForm(APROVADOR_VAZIO);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível remover o aprovador.');
    } finally { setSalvando(false); }
  };

  const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: 'var(--portal-muted)' };
  const inputStyle = {
    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--portal-border)',
    background: 'var(--portal-bg)', color: 'var(--portal-text)', fontSize: '0.9rem', width: '100%',
  };

  return (
    <ModalBase titulo="Aprovadores e limites de valor" onClose={onClose} largura={620}>
      <p style={{ margin: '0 0 16px', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
        Quem abre a solicitação escolhe o aprovador entre os que cobrem o valor estimado.
        O aviso vai pelo WhatsApp (Martelinho) só para a pessoa escolhida.
        Deixe o limite em branco para quem aprova qualquer valor.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
        padding: 12, borderRadius: 10, border: '1px solid var(--portal-border)',
        background: 'var(--portal-surface-soft)', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1', minWidth: 0 }}>
          <label style={labelStyle}>Contato</label>
          {form.contato ? (
            <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong>{form.contato.push_name || formatTelefone(form.contato.numero)}</strong>
                <span className="cell-muted" style={{ marginLeft: 8, fontSize: '0.8rem' }}>
                  <i className="bi bi-whatsapp me-1" />{formatTelefone(form.contato.numero)}
                </span>
              </span>
              <button type="button" onClick={() => set('contato', null)} disabled={salvando} title="Trocar contato" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--portal-muted)' }}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ) : (
            <div>
              <input
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setErro(''); }}
                placeholder="Buscar contato por nome ou número"
                style={inputStyle}
                disabled={salvando}
              />
              <div style={{
                marginTop: 4, maxHeight: 180, overflowY: 'auto', borderRadius: 8,
                border: '1px solid var(--portal-border)', background: 'var(--portal-bg)',
              }}>
                {buscando && contatos.length === 0 ? (
                  <div className="cell-muted" style={{ padding: '8px 12px', fontSize: '0.82rem' }}>Buscando...</div>
                ) : contatos.length === 0 ? (
                  <div className="cell-muted" style={{ padding: '8px 12px', fontSize: '0.82rem' }}>
                    Nenhum contato encontrado. Cadastre em Atendimento → Contatos.
                  </div>
                ) : contatos.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => set('contato', c)}
                    style={{
                      display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8,
                      padding: '6px 12px', background: 'none', border: 'none',
                      borderBottom: '1px solid var(--portal-border)', cursor: 'pointer',
                      color: 'var(--portal-text)', fontSize: '0.85rem', textAlign: 'left',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.push_name || 'Sem nome'}
                      {(c.filial || c.setor) && (
                        <span className="cell-muted" style={{ marginLeft: 6, fontSize: '0.75rem' }}>
                          {[c.filial, c.setor].filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </span>
                    <span className="cell-muted" style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{formatTelefone(c.numero)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={labelStyle}>Aprova até (R$)</label>
          <input value={form.valor_limite} onChange={(e) => set('valor_limite', e.target.value)} placeholder="Sem limite" inputMode="decimal" style={inputStyle} disabled={salvando} />
        </div>
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {form.id && (
            <button type="button" onClick={() => { setForm(APROVADOR_VAZIO); setBusca(''); setErro(''); }} disabled={salvando} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'none', cursor: 'pointer', color: 'var(--portal-muted)' }}>
              Cancelar edição
            </button>
          )}
          <button type="button" onClick={salvar} disabled={salvando} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: '#2A8A9C', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            <i className={`bi ${form.id ? 'bi-check-lg' : 'bi-plus-lg'} me-1`} />
            {salvando ? 'Salvando...' : form.id ? 'Salvar alterações' : 'Cadastrar'}
          </button>
        </div>
      </div>

      {erro && <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12, fontSize: '0.82rem' }}>{erro}</div>}

      {carregando ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>Carregando...</div>
      ) : aprovadores.length === 0 ? (
        <div className="cell-muted" style={{ fontSize: '0.85rem' }}>
          Nenhum aprovador cadastrado. Enquanto a lista estiver vazia, a solicitação não mostra a opção de escolher o aprovador e o aviso vai para quem está inscrito nele.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {aprovadores.map((a) => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '8px 12px', borderRadius: 10,
              border: `1px solid ${form.id === a.id ? '#2A8A9C' : 'var(--portal-border)'}`,
              background: 'var(--portal-surface-soft)',
            }}>
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--portal-text)' }}>{a.nome}</div>
                <div className="cell-muted" style={{ fontSize: '0.78rem' }}>
                  <i className="bi bi-whatsapp me-1" />{formatTelefone(a.numero)}
                </div>
              </div>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{rotuloLimite(a)}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={salvando} onClick={() => editar(a)} title="Editar" style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                  <i className="bi bi-pencil" />
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" disabled={salvando} onClick={() => remover(a)} title="Remover" style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                  <i className="bi bi-trash" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ModalBase>
  );
}

export default function Solicitacoes() {
  const { temPermissao } = useAuth();
  const isGestor = temPermissao('ti:equipamentos');

  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');

  const [modalNova, setModalNova] = useState(false);
  const [modalAceitar, setModalAceitar] = useState(null);
  const [modalRecusar, setModalRecusar] = useState(null);
  const [modalDetalhes, setModalDetalhes] = useState(null);
  const [modalAprovadores, setModalAprovadores] = useState(false);
  const [atualizando, setAtualizando] = useState(null);

  const carregar = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFiltro) params.status = statusFiltro;
    if (tipoFiltro) params.tipo_solicitacao = tipoFiltro;
    axios.get(`${API}/solicitacoes`, { params })
      .then((res) => setSolicitacoes(res.data?.data || []))
      .catch(() => setErro('Nao foi possivel carregar as solicitacoes.'))
      .finally(() => setLoading(false));
  }, [statusFiltro, tipoFiltro]);

  useEffect(() => { carregar(); }, [carregar]);

  const atualizarStatus = async (id, status, extra = {}) => {
    setAtualizando(id);
    try {
      const res = await axios.patch(`${API}/solicitacoes/${id}/status`, { status, ...extra });
      setSolicitacoes((prev) => prev.map((s) => (s.id === id ? res.data?.data : s)));
    } catch (e) {
      setErro(e.response?.data?.message || 'Erro ao atualizar status.');
    } finally { setAtualizando(null); }
  };

  const onSalvoNova = (nova) => setSolicitacoes((prev) => [nova, ...prev]);
  const onSalvoStatus = (atualizada) => {
    setSolicitacoes((prev) => prev.map((s) => (s.id === atualizada.id ? atualizada : s)));
    setModalAceitar(null);
    setModalRecusar(null);
  };

  const btnStyle = (bg, disabled) => ({
    padding: '4px 12px', borderRadius: 6, border: 'none', background: disabled ? '#ccc' : bg,
    color: '#fff', cursor: disabled ? 'default' : 'pointer', fontSize: '0.78rem', fontWeight: 600,
    opacity: disabled ? 0.6 : 1,
  });

  const pendentes = solicitacoes.filter((s) => s.status === 'pendente').length;

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Solicitacoes de Equipamentos</h1>
          <p>Solicitacoes de equipamentos novos ou trocas encaminhadas ao setor de TI.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isGestor && pendentes > 0 && (
            <span style={{ background: 'rgba(253,126,20,0.15)', color: '#fd7e14', border: '1px solid rgba(253,126,20,0.4)', borderRadius: 20, padding: '3px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
              {pendentes} pendente{pendentes !== 1 ? 's' : ''}
            </span>
          )}
          {isGestor && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setModalAprovadores(true)}
              title="Quem aprova e até qual valor"
            >
              <i className="bi bi-person-check me-1" /> Aprovadores
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setModalNova(true)}
            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-lg me-1" /> Nova Solicitacao
          </button>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field">
          <select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {isGestor && (
          <div className="filter-card__field">
            <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
              <option value="">Todos os tipos</option>
              {Object.entries(TIPO_SOLICITACAO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
      </div>

      {erro && (
        <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>
          {erro}
          <button type="button" onClick={() => setErro('')} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando solicitacoes...</p>
          </div>
        ) : solicitacoes.length === 0 ? (
          <div className="empty-state">
            <strong>Nenhuma solicitacao encontrada</strong>
            <span>Clique em &quot;Nova Solicitacao&quot; para encaminhar um pedido ao setor de TI.</span>
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tipo</th>
                  <th>Equipamento</th>
                  <th>Descricao</th>
                  <th>Valor</th>
                  <th style={{ textAlign: 'center' }} title="Anexos"><i className="bi bi-paperclip" /></th>
                  <th>Urg.</th>
                  {isGestor && <th>Solicitante</th>}
                  {isGestor && <th>Filial / Setor</th>}
                  <th>Status</th>
                  <th>Data</th>
                  {isGestor && <th>Acoes</th>}
                </tr>
              </thead>
              <tbody>
                {solicitacoes.map((s) => {
                  const cor = STATUS_COR[s.status];
                  const ocupado = atualizando === s.id;
                  return (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setModalDetalhes(s)}>
                      <td className="cell-muted" style={{ fontWeight: 600 }}>#{s.id}</td>
                      <td><Chip label={TIPO_SOLICITACAO_LABEL[s.tipo_solicitacao] || s.tipo_solicitacao} cores={TIPO_COR[s.tipo_solicitacao]} /></td>
                      <td>{TIPO_EQUIPAMENTO_LABEL[s.tipo_equipamento] || s.tipo_equipamento}</td>
                      <td style={{ maxWidth: 220 }}>
                        <span title={s.descricao} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.descricao}
                        </span>
                      </td>
                      <td className="cell-muted" style={{ whiteSpace: 'nowrap', fontWeight: s.valor_estimado ? 600 : 400 }}>
                        {formatBRL(s.valor_estimado)}
                      </td>
                      <td style={{ textAlign: 'center' }} className="cell-muted">
                        {s.anexos && s.anexos.length > 0
                          ? <span title={`${s.anexos.length} anexo(s)`}><i className="bi bi-paperclip" /> {s.anexos.length}</span>
                          : <span>-</span>}
                      </td>
                      <td>
                        {s.urgente
                          ? <span style={{ color: '#dc3545', fontWeight: 700, fontSize: '0.85rem' }}>Sim</span>
                          : <span className="cell-muted">-</span>}
                      </td>
                      {isGestor && <td className="cell-muted">{s.solicitante || '-'}</td>}
                      {isGestor && (
                        <td className="cell-muted" style={{ fontSize: '0.8rem' }}>
                          {[s.solicitante_filial, s.solicitante_setor].filter(Boolean).join(' / ') || '-'}
                        </td>
                      )}
                      <td><Chip label={STATUS_LABEL[s.status] || s.status} cores={cor} /></td>
                      <td className="cell-muted" style={{ whiteSpace: 'nowrap' }}>{formatDateBR(s.created_at)}</td>
                      {isGestor && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                            {s.status === 'pendente' && (
                              <>
                                <button
                                  type="button"
                                  style={btnStyle('#28a745', ocupado)}
                                  disabled={ocupado}
                                  onClick={() => setModalAceitar(s)}
                                >
                                  {ocupado ? '...' : 'Aceitar'}
                                </button>
                                <button
                                  type="button"
                                  style={btnStyle('#dc3545', ocupado)}
                                  disabled={ocupado}
                                  onClick={() => setModalRecusar(s)}
                                >
                                  Recusar
                                </button>
                              </>
                            )}
                            {s.status === 'aceita' && (
                              <button
                                type="button"
                                style={btnStyle('#2A8A9C', ocupado)}
                                disabled={ocupado}
                                onClick={() => atualizarStatus(s.id, 'concluida')}
                              >
                                {ocupado ? '...' : 'Concluir'}
                              </button>
                            )}
                            {(s.status === 'recusada' || s.status === 'concluida') && (
                              <span className="cell-muted" style={{ fontSize: '0.78rem' }}>-</span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalNova && <ModalNova onClose={() => setModalNova(false)} onSalvo={onSalvoNova} />}
      {modalAprovadores && <GerenciarAprovadores onClose={() => setModalAprovadores(false)} />}
      {modalAceitar && <ModalAceitar solicitacao={modalAceitar} onClose={() => setModalAceitar(null)} onSalvo={onSalvoStatus} />}
      {modalRecusar && <ModalRecusar solicitacao={modalRecusar} onClose={() => setModalRecusar(null)} onSalvo={onSalvoStatus} />}
      {modalDetalhes && (
        <ModalDetalhes
          solicitacao={modalDetalhes}
          onClose={() => setModalDetalhes(null)}
          onMudou={(atualizada) => {
            setSolicitacoes((prev) => prev.map((s) => (s.id === atualizada.id ? atualizada : s)));
            setModalDetalhes(atualizada);
          }}
        />
      )}
    </div>
  );
}
